import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, Plus, Search, ArrowLeft, Phone, Mail, MapPin, 
  Edit2, Trash2, X, Globe
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { securityCompaniesAPI } from '../services/api';

interface SecurityCompany {
  id: string;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  contractStart?: string;
  contractEnd?: string;
}

// Mock data removed — page fetches real data from securityCompaniesAPI

export default function EmpresasSeguridad() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<SecurityCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<SecurityCompany | null>(null);

  const { isAdmin } = useAuth();

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await securityCompaniesAPI.getAll(condoId);
      const data = response.data || response || [];
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading companies:', err);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar esta empresa?')) {
      setCompanies(prev => prev.filter(c => c.id !== id));
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Empresas de Seguridad</h1>
              <p className="text-xs text-gray-500">Contratos de vigilancia</p>
            </div>
          </div>
          {isAdmin() && (
            <button onClick={() => setShowNewModal(true)} className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2">
              <Plus className="w-4 h-4" /> Agregar Empresa
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <div className="grid gap-4">
          {companies.map(company => (
            <div key={company.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#2D3748]">{company.name}</h3>
                    <p className="text-sm text-gray-500">Contacto: {company.contactName}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><Mail className="w-4 h-4" /> {company.email}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-4 h-4" /> {company.phone}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin className="w-4 h-4" /> {company.address}</p>
                    {company.website && (
                      <p className="text-sm text-[#36CCD8] flex items-center gap-1"><Globe className="w-4 h-4" /> {company.website}</p>
                    )}
                  </div>
                </div>
                {isAdmin() && (
                  <div className="flex gap-2">
                    <button onClick={() => setEditingCompany(company)} className="p-2 text-gray-400 hover:text-[#36CCD8]"><Edit2 className="w-5 h-5" /></button>
                    <button onClick={() => handleDelete(company.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="w-5 h-5" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {(showNewModal || editingCompany) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] mb-6">{editingCompany ? 'Editar' : 'Nueva'} Empresa</h3>
            <form className="space-y-4">
              <input type="text" placeholder="Nombre de empresa" defaultValue={editingCompany?.name} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="text" placeholder="Nombre del contacto" defaultValue={editingCompany?.contactName} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="email" placeholder="Email" defaultValue={editingCompany?.email} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="tel" placeholder="Teléfono" defaultValue={editingCompany?.phone} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="text" placeholder="Dirección" defaultValue={editingCompany?.address} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowNewModal(false); setEditingCompany(null); }} className="flex-1 py-3 border border-gray-200 rounded-xl">Cancelar</button>
                <button type="button" onClick={() => { setShowNewModal(false); setEditingCompany(null); alert('✅ Guardado'); }} className="flex-1 py-3 bg-[#36CCD8] text-white rounded-xl">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
